import { useCallback, useEffect, useMemo } from 'react';
import { DrawerLayout } from 'components/DrawerLayout/DrawerLayout';
import dayjs from 'dayjs';
import { useResponsiveDrawerColumns } from 'hooks/useResponsiveDrawerColumns';
import { getText } from 'i18n/intl';
import { startCase } from 'lodash';
import { fetchFields } from 'slices/field.slice';
import { fetchOrganizationHierarchy } from 'slices/organizationHierarchy.slice';
import { useAppDispatch } from 'slices/store';
import { requiredValidationRule } from 'utils/validations';
import type {
  ObjectMapping,
  SyncSetting,
  SyncSettingResponse,
} from '@timeedit/calendar-integration-api-contract';
import type { TTEObject, TType } from '@timeedit/types';
import { TEDrawerSection, TEInput } from '@timeedit/ui-components';
import {
  Collapse,
  DatePicker,
  Flex,
  Form,
  type FormInstance,
  Select,
} from '@timeedit/ui-components/antd';
import type { RangePickerProps } from '@timeedit/ui-components/antd/date-picker/index';
import { NEW_ITEM_ID } from '../shared';
import EventInfoSection, {
  type EventInfoSectionProps,
} from './EventInfoSection';
import EventReservationMapper from './EventReservationMapper';
import SyncObjectFilter from './SyncObjectFilter';
import { SyncObjectTable } from './SyncObjectTable';

const { RangePicker } = DatePicker;

export const intlPrefix = 'integration.calendar.sync_settings';

export type CreateSyncSetting = Omit<SyncSetting, 'id'>;
type DateTuple = Parameters<
  Exclude<RangePickerProps['onChange'], undefined>
>[0];

type Props = {
  syncSetting: SyncSettingResponse | null | undefined;
  form: FormInstance<SyncSettingResponse>;
  onValuesChange: (
    changedValues:
      | Partial<SyncSettingResponse>
      | { syncMode: Partial<SyncSettingResponse['syncMode']> },
    values: SyncSettingResponse
  ) => void;
  objectMapping: ObjectMapping | null;
  objectTypes: TType[];
};

const MS365SyncSettingsDetails = ({
  syncSetting,
  form,
  objectMapping,
  objectTypes,
  onValuesChange,
}: Props) => {
  const dispatch = useAppDispatch();

  const { formLayout, nbrOfColumns } = useResponsiveDrawerColumns({
    xxl: 2,
    xl: 2,
    lg: 2,
    sm: 1,
  });

  const syncType = Form.useWatch('syncType', form);
  const syncObjectFilter = Form.useWatch('syncObjectFilter', form);
  const objectIds = Form.useWatch('objectIds', form);
  const syncTarget = Form.useWatch('syncTarget', form);
  const syncMode = Form.useWatch(['syncMode', 'mode'], form);
  const syncPeriod = Form.useWatch('syncPeriod', form);

  const defaultDateRange = useMemo<RangePickerProps['defaultValue']>(
    () =>
      syncPeriod?.type === 'STATIC'
        ? [dayjs(syncPeriod.startDate), dayjs(syncPeriod.endDate)]
        : [null, null],
    [syncPeriod]
  );

  const intervalValue = useMemo<number | null>(() => {
    if (syncPeriod?.type === 'DYNAMIC') {
      const days = Number(syncPeriod?.duration?.replace(/P|D/g, ''));
      return Number.isNaN(days) ? null : days;
    }

    return null;
  }, [syncPeriod]);

  const objectType = useMemo(
    () =>
      objectTypes.find((o) => {
        if (syncType === 'ROOM') {
          return o.id === objectMapping?.room?.objectTypeId;
        } else if (syncType === 'STAFF') {
          return o.id === objectMapping?.staff?.objectTypeId;
        } else if (syncType === 'STUDENT') {
          return o.id === objectMapping?.student?.objectTypeId;
        }
        return false;
      }),
    [objectMapping, objectTypes, syncType]
  );

  const setFieldValue = useCallback(
    <Key extends keyof SyncSettingResponse>(
      name: Key,
      value: SyncSettingResponse[Key]
    ) => {
      form.setFieldValue(name, value);
      onValuesChange({ [name]: value }, form.getFieldsValue());
    },
    [form, onValuesChange]
  );

  const setSyncModeFieldValue = useCallback<EventInfoSectionProps['onChange']>(
    (name, value) => {
      const currentSyncMode = form.getFieldValue(
        'syncMode'
      ) as SyncSettingResponse['syncMode'];
      const newSyncMode = { ...currentSyncMode, [name]: value };
      form.setFieldValue('syncMode', newSyncMode);
      onValuesChange({ syncMode: { [name]: value } }, form.getFieldsValue());
    },
    [form, onValuesChange]
  );

  const onRangeChange = useCallback<
    Exclude<RangePickerProps['onChange'], undefined>
  >(
    (dates) => {
      if (syncPeriod?.type !== 'STATIC') {
        return;
      }

      const newSyncPeriod = createSyncPeriod(dates);
      const isUnchanged =
        newSyncPeriod.startDate === syncPeriod?.startDate &&
        newSyncPeriod.endDate === syncPeriod?.endDate;

      if (isUnchanged) {
        return;
      }

      setFieldValue('syncPeriod', newSyncPeriod);
    },
    [syncPeriod, setFieldValue]
  );

  const onIntervalChange = useCallback(
    (val: number | null) => {
      setFieldValue('syncPeriod', {
        duration: `P${val || 0}D`,
        type: 'DYNAMIC',
      });
    },
    [setFieldValue]
  );

  useEffect(() => {
    const syncPeriod = createSyncPeriod(defaultDateRange);
    onRangeChange(defaultDateRange, [syncPeriod.startDate, syncPeriod.endDate]);
  }, [defaultDateRange, onRangeChange]);

  useEffect(() => {
    dispatch(fetchFields());
    dispatch(fetchOrganizationHierarchy());
  }, [dispatch]);

  const onObjectsChanged = useCallback(
    (objects?: TTEObject[]) => {
      const newObjectIds = objects?.map(({ id }) => id) ?? [];

      const isUnchanged =
        objectIds &&
        objectIds.length === newObjectIds.length &&
        objectIds.every((id, index) => newObjectIds[index] === id);
      if (isUnchanged) {
        return;
      }

      setFieldValue('objectIds', newObjectIds);
    },
    [objectIds, setFieldValue]
  );

  const onObjectsRemoved = useCallback(
    (objects: TTEObject[]) => {
      const objectIdSet = new Set(objects.map(({ id }) => id));
      const newObjectIds = objectIds.filter((id) => !objectIdSet.has(id));

      setFieldValue('objectIds', newObjectIds);
    },
    [objectIds, setFieldValue]
  );

  const syncTypeOptions = useMemo(() => {
    if (!objectMapping) {
      return [];
    }

    const { id: _id, ...properties } = objectMapping;

    return Object.entries(properties)
      .filter(([, value]) => !!value?.objectTypeId)
      .map(([key]) => ({
        label: startCase(key),
        value: key.toUpperCase(),
      }));
  }, [objectMapping]);

  const handleSyncModeChanges = useCallback(
    (syncModeChanges: Partial<SyncSettingResponse['syncMode']> = {}) => {
      if (Object.hasOwn(syncModeChanges ?? {}, 'mode')) {
        if (syncModeChanges.mode === 'TE_IN') {
          form.setFieldsValue({
            syncMode: {
              descriptionFields: undefined,
              subjectTemplates: undefined,
              locationTemplates: undefined,
            },
          });
        } else if (syncModeChanges.mode === 'TE_OUT') {
          form.setFieldsValue({
            syncMode: {
              reservationTemplateId: undefined,
              subjectFieldId: undefined,
              organiserFieldId: undefined,
            },
          });
        }

        return;
      }

      // Reset the form fields that are dependent on the selected reservation template when the selected template is changed
      if (Object.hasOwn(syncModeChanges ?? {}, 'reservationTemplateId')) {
        form.setFieldsValue({
          syncMode: { subjectFieldId: undefined, organiserFieldId: undefined },
        });
      }
    },
    [form]
  );

  const onFormValuesChange = useCallback<Props['onValuesChange']>(
    (changedValues) => {
      if (Object.hasOwn(changedValues, 'syncType')) {
        form.setFieldsValue({
          objectIds: [],
          syncTarget: 'ALL',
        });
      }

      if (Object.hasOwn(changedValues, 'reservationModeId')) {
        form.setFieldsValue({
          syncMode: {
            reservationTemplateId: undefined,
            subjectFieldId: undefined,
            organiserFieldId: undefined,
          },
        });
      }

      handleSyncModeChanges(changedValues.syncMode);

      onValuesChange(changedValues, form.getFieldsValue());
    },
    [form, onValuesChange, handleSyncModeChanges]
  );

  const onSyncTargetChange = useCallback(
    (value: SyncSettingResponse['syncTarget']) => {
      form.setFieldsValue({
        syncTarget: value,
        objectIds: [],
      });
      onValuesChange(
        { syncTarget: value, objectIds: [] },
        form.getFieldsValue()
      );
    },
    [form, onValuesChange]
  );

  return (
    <div>
      <Form
        {...formLayout}
        labelAlign="left"
        size="middle"
        form={form}
        initialValues={syncSetting ?? {}}
        onValuesChange={onFormValuesChange}
      >
        <TEDrawerSection label={getText(`${intlPrefix}.title`)}>
          <DrawerLayout numberOfColumns={nbrOfColumns}>
            <Form.Item
              label={getText(`${intlPrefix}.form.name.label`)}
              name="name"
              rules={[requiredValidationRule]}
              tooltip={getText(`${intlPrefix}.form.name.description`)}
            >
              <TEInput.Input size="small" />
            </Form.Item>
            <Form.Item
              label={getText(`${intlPrefix}.form.sync_type.label`)}
              name="syncType"
              rules={[requiredValidationRule]}
              tooltip={getText(`${intlPrefix}.form.sync_type.description`)}
            >
              <Select
                placeholder="Select type of object to sync"
                options={syncTypeOptions}
                size="small"
              />
            </Form.Item>
            <Form.Item
              label={getText(`${intlPrefix}.form.sync_mode.label`)}
              name={['syncMode', 'mode']}
              rules={[requiredValidationRule]}
              tooltip={getText(`${intlPrefix}.form.sync_mode.description`)}
            >
              <Select
                placeholder="Select sync mode"
                options={syncModeOptions}
                size="small"
                disabled={syncSetting?.id !== NEW_ITEM_ID}
              />
            </Form.Item>
            <Form.Item
              label={getText(`${intlPrefix}.form.sync_period.label`)}
              name={['syncPeriod', 'type']}
              rules={[requiredValidationRule]}
              tooltip={getText(`${intlPrefix}.form.sync_period.description`)}
            >
              <Select
                placeholder={getText(
                  `${intlPrefix}.form.sync_period.placeholder`
                )}
                options={syncPeriodOptions}
                size="small"
              />
            </Form.Item>
            {syncPeriod?.type === 'STATIC' && (
              <Form.Item
                required
                label={getText(
                  `${intlPrefix}.form.sync_period.date_range.label`
                )}
              >
                <RangePicker
                  allowClear={false}
                  value={defaultDateRange}
                  onChange={onRangeChange}
                  size="small"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}
            {syncPeriod?.type === 'DYNAMIC' && (
              <Form.Item
                required
                label={getText(`${intlPrefix}.form.sync_period.interval.label`)}
                tooltip={getText(
                  `${intlPrefix}.form.sync_period.interval.tooltip`
                )}
              >
                <TEInput.InputNumber
                  size="small"
                  value={intervalValue}
                  min={1}
                  max={365}
                  onChange={onIntervalChange}
                />
              </Form.Item>
            )}
          </DrawerLayout>
          <Form.Item hidden name={['syncMode', 'subjectTemplates']} />
          <Form.Item hidden name={['syncMode', 'locationTemplates']} />
          <Form.Item hidden name={['syncMode', 'descriptionFields']} />
          <Form.Item hidden name={['syncMode', 'reservationModeIds']} />
          <Form.Item hidden name={['syncMode', 'reservationStatuses']} />
          <Form.Item hidden name={['syncMode', 'reminder', 'behavior']} />
          <Form.Item
            hidden
            name={['syncMode', 'reminder', 'minutesBeforeStart']}
          />
          <Form.Item hidden name={['syncPeriod', 'startDate']} />
          <Form.Item hidden name={['syncPeriod', 'endDate']} />
          <Form.Item hidden name={['syncPeriod', 'duration']} />
          <Form.Item hidden name="id" />
          <Form.Item hidden name="objectIds" />
          <Form.Item hidden name="syncTarget" />
          <Form.Item hidden name="syncObjectFilter" />
        </TEDrawerSection>
        {['TE_OUT', 'TE_TWO_WAY'].includes(syncMode) && (
          <EventInfoSection
            form={form}
            label={getText(`${intlPrefix}.form.sync_mode.te_out.label`)}
            onChange={setSyncModeFieldValue}
          />
        )}
        {['TE_IN', 'TE_TWO_WAY'].includes(syncMode) && (
          <EventReservationMapper
            form={form}
            label={getText(`${intlPrefix}.form.sync_mode.te_in.label`)}
          />
        )}
      </Form>
      <TEDrawerSection label={getText(`${intlPrefix}.form.objects.title`)}>
        <Flex vertical className="te-gap-3">
          <Form.Item
            label={getText(`${intlPrefix}.form.sync_target.label`)}
            rules={[requiredValidationRule]}
            tooltip={getText(`${intlPrefix}.form.sync_target.description`)}
            style={{ maxWidth: 500 }}
          >
            <Select
              placeholder={getText(
                `${intlPrefix}.form.sync_target.placeholder`
              )}
              options={syncTargetOptions}
              size="small"
              value={syncTarget}
              onChange={onSyncTargetChange}
            />
          </Form.Item>
          {objectType && (
            <TEInput.PureText
              label="Object type"
              value={`${objectType.name} (${objectType.extId})`}
            />
          )}
          {syncTarget === 'SELECTED' && (
            <Collapse
              items={[
                {
                  key: 'sync-object',
                  label: 'Objects to sync',
                  children: (
                    <SyncObjectTable
                      objectIds={objectIds ?? []}
                      objectType={objectType}
                      onChange={onObjectsChanged}
                      onRemove={onObjectsRemoved}
                    />
                  ),
                },
              ]}
            />
          )}
          {syncTarget === 'FILTERED' && (
            <SyncObjectFilter
              filter={syncObjectFilter}
              objectType={objectType}
              onChange={(filter) => {
                setFieldValue('syncObjectFilter', filter);
              }}
            />
          )}
        </Flex>
      </TEDrawerSection>
    </div>
  );
};

const syncModeOptions = [
  {
    label: getText(
      'integration.calendar.sync_settings.form.sync_mode.te_out.label'
    ),
    value: 'TE_OUT',
  },
  {
    label: getText(
      'integration.calendar.sync_settings.form.sync_mode.te_in.label'
    ),
    value: 'TE_IN',
  },
  {
    label: getText(
      'integration.calendar.sync_settings.form.sync_mode.te_two_way.label'
    ),
    value: 'TE_TWO_WAY',
  },
];

const syncPeriodOptions = [
  {
    label: getText(`${intlPrefix}.form.sync_period.static.label`),
    value: 'STATIC',
  },
  {
    label: getText(`${intlPrefix}.form.sync_period.relative.label`),
    value: 'DYNAMIC',
  },
];

const syncTargetOptions = [
  {
    label: getText(`${intlPrefix}.form.sync_target.option.all`),
    value: 'ALL',
  },
  {
    label: getText(`${intlPrefix}.form.sync_target.option.filtered`),
    value: 'FILTERED',
  },
  {
    label: getText(`${intlPrefix}.form.sync_target.option.selected`),
    value: 'SELECTED',
  },
];

function createSyncPeriod(dates: DateTuple) {
  const startDate = dates?.[0]?.format('YYYY-MM-DD');
  const endDate = dates?.[1]?.format('YYYY-MM-DD');

  return {
    type: 'STATIC',
    startDate,
    endDate,
  } as const;
}

export default MS365SyncSettingsDetails;
